import React, { useState } from 'react';
import axios from 'axios';
import { Edit, Trash, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Product } from '@/types/product';
import { useToast } from '@/hooks/use-toast';
import ProductDialog from './ProductDialog';
import DeleteConfirmationDialog from './DeleteConfirmationDialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

interface ProductsTabProps {
  products: Product[];
  isLoading: boolean;
  groups: any[];
  tags: any[];
  fetchProducts: () => Promise<void>;
}

const ProductsTab: React.FC<ProductsTabProps> = ({
  products,
  isLoading,
  groups,
  tags,
  fetchProducts
}) => {
  const [currentProduct, setCurrentProduct] = useState<Product | null>(null);
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [isProductDialogOpen, setIsProductDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();

  const handleOpenProductDialog = (product?: Product) => {
    if (product) {
      setCurrentProduct(product);
      setSelectedTags(product.tags || []);
    } else {
      setCurrentProduct({
        id: '',
        title: '',
        description: '',
        image: '/placeholder.svg',
        rating: 4.5,
        group_id: null,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        tags: [],
      });
      setSelectedTags([]);
    }
    setIsProductDialogOpen(true);
  };

  const handleSaveProduct = async () => {
    if (!currentProduct) return;
    
    try {
      setIsSubmitting(true);
      
      // Validate required fields
      if (!currentProduct.title.trim()) {
        toast({
          title: "Validation Error",
          description: "Product title is required",
          variant: "destructive",
        });
        setIsSubmitting(false);
        return;
      }
      
      const productData = {
        title: currentProduct.title.trim(),
        description: currentProduct.description,
        image: currentProduct.image,
        rating: currentProduct.rating,
        group_id: currentProduct.group_id,
      };
      
      const config = {
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      };
      
      let response;
      if (!currentProduct.id) {
        response = await axios.post('/api/products', productData, config);
      } else {
        response = await axios.put(`/api/products/${currentProduct.id}`, productData, config);
      }
      
      const savedProduct = response.data;
      
      // Handle tags if necessary
      if (savedProduct && savedProduct.id) {
        // Fetch current tags for the product
        const currentTagsResponse = await axios.get(`/api/products/${savedProduct.id}/tags`);
        const currentTags = currentTagsResponse.data || [];
        
        // Map tag names to tag objects
        const availableTagMap = new Map(tags.map(tag => [tag.name, tag.id]));
        
        // Handle tag associations
        if (selectedTags.length > 0) {
          // Find new tags to add
          for (const tagName of selectedTags) {
            const tagId = availableTagMap.get(tagName);
            if (tagId && !currentTags.some((t: any) => t.id === tagId)) {
              await axios.post(`/api/tags/product/${savedProduct.id}/${tagId}`, {}, config);
            }
          }
          
          // Find tags to remove
          for (const tag of currentTags) {
            if (!selectedTags.includes(tag.name)) {
              await axios.delete(`/api/tags/product/${savedProduct.id}/${tag.id}`, config);
            }
          }
        } else {
          // Remove all tags if selectedTags is empty
          for (const tag of currentTags) {
            await axios.delete(`/api/tags/product/${savedProduct.id}/${tag.id}`, config);
          }
        }
      }
      
      toast({
        title: currentProduct.id ? "Product updated" : "Product added",
        description: `${currentProduct.title} has been ${currentProduct.id ? 'updated' : 'added'} successfully.`,
      });
      
      await fetchProducts();
      setIsProductDialogOpen(false);
    } catch (error: any) {
      console.error('Error saving product:', error);
      toast({
        title: "Error saving product",
        description: error.response?.data?.message || "There was a problem saving the product data.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!currentProduct) return;
    
    try {
      setIsSubmitting(true);
      
      const config = {
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      };
      
      await axios.delete(`/api/products/${currentProduct.id}`, config);
      toast({
        title: "Product deleted",
        description: `${currentProduct.title} has been deleted.`,
        variant: "destructive",
      });
      await fetchProducts();
      setIsDeleteDialogOpen(false);
    } catch (error: any) {
      console.error('Error deleting product:', error);
      toast({
        title: "Error deleting product",
        description: error.response?.data?.message || "There was a problem deleting the product.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const openDeleteDialog = (product: Product) => {
    setCurrentProduct(product);
    setIsDeleteDialogOpen(true);
  };

  const handleTagSelect = (tagName: string) => {
    setSelectedTags((prev) => {
      if (prev.includes(tagName)) {
        return prev.filter(t => t !== tagName);
      } else {
        return [...prev, tagName];
      }
    });
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold">Product Management</h1>
        <Button onClick={() => handleOpenProductDialog()}>
          <Plus className="h-4 w-4 mr-2" />
          Add Product
        </Button>
      </div>
      
      <div className="bg-white dark:bg-gray-800 rounded-md shadow">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Title</TableHead>
              <TableHead>Description</TableHead>
              <TableHead>Group</TableHead>
              <TableHead>Tags</TableHead>
              <TableHead>Rating</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
                </TableCell>
              </TableRow>
            ) : products.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-8">
                  No products found.
                </TableCell>
              </TableRow>
            ) : (
              products.map((product) => (
                <TableRow key={product.id}>
                  <TableCell className="font-medium">{product.title}</TableCell>
                  <TableCell className="max-w-xs truncate">{product.description}</TableCell>
                  <TableCell>{product.group_name || 'None'}</TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1">
                      {Array.isArray(product.tags) && product.tags.map((tag, idx) => (
                        <span key={idx} className="px-2 py-1 bg-gray-100 dark:bg-gray-700 rounded-full text-xs">
                          {tag}
                        </span>
                      ))}
                    </div>
                  </TableCell>
                  <TableCell>{product.rating}</TableCell>
                  <TableCell className="text-right">
                    <Button 
                      variant="ghost" 
                      size="icon" 
                      onClick={() => handleOpenProductDialog(product)}
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button 
                      variant="ghost" 
                      size="icon"
                      onClick={() => openDeleteDialog(product)}
                    >
                      <Trash className="h-4 w-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <ProductDialog
        isOpen={isProductDialogOpen}
        setIsOpen={setIsProductDialogOpen}
        currentProduct={currentProduct}
        setCurrentProduct={setCurrentProduct}
        selectedTags={selectedTags}
        onTagSelect={handleTagSelect}
        groups={groups}
        tags={tags}
        onSave={handleSaveProduct}
        isSubmitting={isSubmitting}
      />

      <DeleteConfirmationDialog
        isOpen={isDeleteDialogOpen}
        setIsOpen={setIsDeleteDialogOpen}
        title="Confirm Delete"
        message={`Are you sure you want to delete "${currentProduct?.title}"? This action cannot be undone.`}
        onDelete={handleDelete}
        isSubmitting={isSubmitting}
      />
    </div>
  );
};

export default ProductsTab;
